import {IFirebaseQuery} from '../../shared-models/firebase/firebase-queries';
import {
  endAt,
  endBefore,
  limit,
  orderBy,
  Query,
  query,
  startAfter,
  startAt,
  Timestamp,
  where,
} from '@angular/fire/firestore';

export function generateFirebaseCollectionReference(
  firebaseQueries: IFirebaseQuery[],
  baseQuery: Query,
): Query {
  const constraints = [];

  for (const firebaseQuery of firebaseQueries) {
    switch (firebaseQuery.q) {
      case 'where':
        constraints.push(where(...(firebaseQuery.p as Parameters<typeof where>)));
        break;
      case 'orderBy':
        constraints.push(orderBy(...(firebaseQuery.p as Parameters<typeof orderBy>)));
        break;
      case 'limit':
        constraints.push(limit(...(firebaseQuery.p as Parameters<typeof limit>)));
        break;
      case 'startAt':
        constraints.push(startAt(...(firebaseQuery.p as Parameters<typeof startAt>)));
        break;
      case 'startAfter':
        constraints.push(startAfter(...(firebaseQuery.p as Parameters<typeof startAfter>)));
        break;
      case 'endAt':
        constraints.push(endAt(...(firebaseQuery.p as Parameters<typeof endAt>)));
        break;
      case 'endBefore':
        constraints.push(endBefore(...(firebaseQuery.p as Parameters<typeof endBefore>)));
        break;
    }
  }

  return query(baseQuery, ...constraints);
}


export function firebaseTimeStampToDate(
  value: {seconds: number; nanoseconds?: number} | Timestamp | Date | string | null | undefined,
): Date | null {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  if (value instanceof Timestamp) {
    return value.toDate();
  }

  if (typeof value === 'string') {
    const parsedDate: Date = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  if (typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
    return value.toDate();
  }

  if (typeof value === 'object' && 'seconds' in value && typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000 + (value.nanoseconds ?? 0) / 1_000_000);
  }

  return null;
}

export function dateToFirebaseTimestamp(date: Date): Timestamp {
  if (!date) {
    return Timestamp.fromDate(new Date(0));
  }
  return Timestamp.fromDate(date);
}

/**
 * Strict URI-component encoding that also escapes * ! ' ( )
 * RFC 3986-compliant – matches what most back-ends and cloud SDKs call
 * “strict URI encoding”.
 *
 * Example:
 *  encodeStrict('IBR0.4*4.2')  // → 'IBR0.4%2A4.2'
 */
export function encodeStrict(value: string): string {
  return encodeURIComponent(value).replace(
    /[!'()*]/g,
    c => '%' + c.charCodeAt(0).toString(16).toUpperCase(),
  );
}

const RAW_STOCK_CODE_DECODE_LIMIT = 5;

/**
 * Decode a stock code or Firestore document ID until it is the raw stock code.
 * Already-raw values are unchanged. Invalid percent sequences are left as-is.
 *
 * Example:
 *  toRawStockCode('ST%2F500BIPRDAM')  // → 'ST/500BIPRDAM'
 *  toRawStockCode('ST/500BIPRDAM')    // → 'ST/500BIPRDAM'
 */
export function toRawStockCode(value: string): string {
  let current: string = value;
  for (let i = 0; i < RAW_STOCK_CODE_DECODE_LIMIT; i++) {
    let decoded: string;
    try {
      decoded = decodeURIComponent(current);
    } catch {
      return current;
    }
    if (decoded === current) {
      return current;
    }
    current = decoded;
  }
  return current;
}

/**
 * Firestore document / chunk key for a stock code. Decodes first so an
 * already-encoded value is not encoded a second time.
 *
 * Example:
 *  toStockDocId('ST/500BIPRDAM')      // → 'ST%2F500BIPRDAM'
 *  toStockDocId('ST%2F500BIPRDAM')    // → 'ST%2F500BIPRDAM'
 */
export function toStockDocId(value: string): string {
  return encodeStrict(toRawStockCode(value));
}
